import "./styles.css";
import { createGame, duplicateGame, fetchGame, getStoredEditToken, updateGame, ApiError } from "./lib/api";
import { GUYMAN_SEED_EDIT_TOKEN, GUYMAN_SLUG } from "./lib/guyman-game";
import { guymanGame } from "./lib/guyman-game";
import { slugify } from "./lib/slug";
import type { Category, Clue, GameData, StoredGame } from "./lib/types";

const appRoot = document.querySelector<HTMLDivElement>("#app")!;

interface PlayState {
  scores: [number, number];
  used: Record<string, boolean>;
  names: [string, string];
  remaining: number;
}

let toastTimer: number | null = null;

window.addEventListener("popstate", () => renderRoute());
window.addEventListener("hashchange", () => renderRoute());
void renderRoute();

async function renderRoute(): Promise<void> {
  const path = currentRoute();
  clearRobots();

  if (path === "/" || path === "") {
    renderHome();
    return;
  }

  if (path === "/new") {
    renderEditor();
    return;
  }

  const playMatch = path.match(/^\/play\/([^/]+)$/);
  if (playMatch) {
    setNoIndex();
    await renderPlay(decodeURIComponent(playMatch[1]));
    return;
  }

  const editMatch = path.match(/^\/edit\/([^/]+)(?:\/([^/]+))?$/);
  if (editMatch) {
    setNoIndex();
    await renderEditor(decodeURIComponent(editMatch[1]), editMatch[2] ? decodeURIComponent(editMatch[2]) : undefined);
    return;
  }

  renderNotFound();
}

function renderHome(): void {
  document.title = "Tribal Trivia";
  appRoot.innerHTML = `
    <main class="home-shell">
      <section class="home-panel">
        <div class="badge-wrap">${badgeSvg()}</div>
        <p class="eyebrow">Reusable Jeopardy-style games</p>
        <h1>Tribal Trivia</h1>
        <p class="home-copy">Create a board, share the play link, and run the room from one big screen.</p>
        <div class="home-actions">
          <a class="button primary" href="#/new" data-link>Create Game</a>
        </div>
      </section>
    </main>
  `;
  wireLinks();
}

async function renderEditor(slug?: string, token?: string): Promise<void> {
  document.title = slug ? "Edit Game" : "Create Game";
  renderLoading("Loading editor");

  let stored: StoredGame | null = null;
  try {
    stored = slug ? await fetchGame(slug) : null;
  } catch (error) {
    renderError(error);
    return;
  }

  const game = stored?.game || makeBlankGame();
  const effectiveToken = token || (slug ? getStoredEditToken(slug) || "" : "");

  appRoot.innerHTML = `
    <main class="builder-shell">
      <header class="builder-top">
        <a class="text-link" href="/" data-link>Tribal Trivia</a>
        <div class="builder-actions">
          ${stored ? `<a class="button" href="#/play/${stored.slug}" data-link>Play</a>` : ""}
          <button class="button primary" id="saveGameButton" type="button">${stored ? "Save Changes" : "Create Game"}</button>
        </div>
      </header>
      <section class="builder-heading">
        <p class="eyebrow">${stored ? "Private edit link" : "New game"}</p>
        <h1>${stored ? "Edit Game" : "Create Game"}</h1>
      </section>
      <form class="builder-form" id="gameForm">
        <section class="form-band">
          <div class="field-grid">
            ${textInput("theme", "Game name", game.theme)}
            ${textInput("title", "Board title", game.title)}
            ${textInput("eyebrow", "Eyebrow", game.eyebrow)}
            ${textInput("subtitle", "Subtitle", game.subtitle)}
            ${textInput("team0", "Team 1", game.teamNames[0])}
            ${textInput("team1", "Team 2", game.teamNames[1])}
            ${numberInput("boardMinutes", "Board minutes", game.boardMinutes)}
            ${textInput("sourceNote", "Source note", game.sourceNote)}
          </div>
        </section>
        <section id="categoryEditor" class="category-editor"></section>
        <section class="form-band">
          <h2>Final Question</h2>
          <div class="field-grid">
            ${textareaInput("finalClue", "Final clue", game.final.clue)}
            ${textareaInput("finalAnswer", "Final response", game.final.answer)}
            ${textInput("finalSource", "Final source", game.final.source || "")}
          </div>
        </section>
      </form>
      <section class="share-panel hidden" id="sharePanel" aria-live="polite">
        <h2>Game Links</h2>
        <div class="share-grid">
          <label>Play link<input id="playLink" readonly></label>
          <label>Edit link<input id="editLink" readonly></label>
        </div>
        <p id="storageNote"></p>
      </section>
      <div class="toast" id="toast" role="status" aria-live="polite"></div>
    </main>
  `;

  renderCategoryEditor(game.categories);
  wireLinks();

  document.querySelector<HTMLButtonElement>("#saveGameButton")?.addEventListener("click", async () => {
    try {
      const nextGame = readGameForm();
      const response = stored
        ? {
            game: await updateGame(stored.slug, effectiveToken, nextGame),
            playUrl: `/play/${stored.slug}`,
            editUrl: `/edit/${stored.slug}/${effectiveToken}`,
            storage: getStoredEditToken(stored.slug) === effectiveToken ? "local" as const : "cloudflare" as const
          }
        : await createGame(nextGame);

      const playLink = absoluteRouteUrl(response.playUrl);
      const editLink = absoluteRouteUrl(response.editUrl);
      document.querySelector<HTMLInputElement>("#playLink")!.value = playLink;
      document.querySelector<HTMLInputElement>("#editLink")!.value = editLink;
      document.querySelector<HTMLElement>("#storageNote")!.textContent =
        response.storage === "local"
          ? "Saved locally for development. When deployed, Cloudflare D1 stores this game."
          : "Saved to Cloudflare D1.";
      document.querySelector<HTMLElement>("#sharePanel")!.classList.remove("hidden");
      showToast(stored ? "Game updated." : "Game created.");

      if (!stored) {
        history.replaceState(null, "", `#${response.editUrl}`);
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Could not save game.");
    }
  });
}

async function renderPlay(slug: string): Promise<void> {
  renderLoading("Loading game");
  let stored: StoredGame;
  try {
    stored = await fetchGame(slug);
  } catch (error) {
    renderError(error);
    return;
  }

  const game = stored.game;
  document.title = `${game.theme} | Tribal Trivia`;
  const state = loadPlayState(stored);
  let active: { categoryIndex: number; clueIndex: number; clue: Clue; category: Category } | null = null;
  let timerId: number | null = null;
  let finalResolved: [boolean, boolean] = [false, false];

  appRoot.innerHTML = `
    <main class="app">
      <header class="topbar">
        <section class="brand" aria-label="Game title">
          ${badgeSvg()}
          <div class="title-block">
            <p class="eyebrow" id="gameEyebrow"></p>
            <h1 id="gameTitle"></h1>
            <p class="subtitle" id="gameSubtitle"></p>
          </div>
        </section>
        <section class="clock" aria-label="Game clock">
          <div class="time" id="timeDisplay"></div>
          <div class="clock-actions">
            <button class="small-btn" id="timerToggle" data-testid="timer-toggle" type="button">Start</button>
            <button class="small-btn secondary" id="timerReset" data-testid="timer-reset" type="button">Reset</button>
          </div>
        </section>
        <section class="scoreboard" aria-label="Team scores">
          ${teamHtml(0, state.names[0])}
          ${teamHtml(1, state.names[1])}
        </section>
      </header>
      <section class="meta" aria-label="Game progress">
        <span id="clueCount"></span>
        <div class="progress" aria-hidden="true"><div class="progress-fill" id="progressFill"></div></div>
        <span>${game.boardMinutes} minute board</span>
      </section>
      <section class="board" id="board" aria-label="Jeopardy board"></section>
      <footer class="footer-actions">
        <div class="host-actions">
          <button class="host-btn primary" id="finalButton" data-testid="final-button" type="button">Final Tribal Council</button>
          <button class="host-btn" id="resetBoard" data-testid="reset-board" type="button">Reset Board</button>
          <button class="host-btn" id="editGameButton" data-testid="edit-game-button" type="button">Edit Game</button>
          <button class="host-btn" id="duplicateButton" data-testid="duplicate-button" type="button">Duplicate Game</button>
          <button class="host-btn" id="fullscreenButton" data-testid="fullscreen-button" type="button">Fullscreen</button>
        </div>
      </footer>
    </main>
    ${clueDialogHtml()}
    ${finalDialogHtml()}
    <div class="toast" id="toast" role="status" aria-live="polite"></div>
  `;

  setText("#gameEyebrow", game.eyebrow);
  setText("#gameTitle", game.title);
  setText("#gameSubtitle", game.subtitle);
  setText("#finalClue", game.final.clue);
  document.querySelector<HTMLElement>("#finalAnswer")!.textContent = game.final.answer;
  setText("#finalSource", game.final.source || "");

  renderBoard();
  renderScores();
  renderTimer();
  wirePlayEvents();

  function wirePlayEvents(): void {
    document.querySelectorAll<HTMLInputElement>("[data-team-name]").forEach((input) => {
      input.addEventListener("input", () => {
        const team = Number(input.dataset.teamName);
        state.names[team] = input.value as never;
        savePlayState(stored, state);
        updateAwardLabels();
      });
    });

    document.querySelectorAll<HTMLButtonElement>("[data-score-team]").forEach((button) => {
      button.addEventListener("click", () => {
        adjustScore(Number(button.dataset.scoreTeam), Number(button.dataset.scoreDelta));
      });
    });

    document.querySelector<HTMLButtonElement>("#timerToggle")?.addEventListener("click", () => {
      if (timerId) {
        window.clearInterval(timerId);
        timerId = null;
      } else {
        timerId = window.setInterval(() => {
          state.remaining = Math.max(0, state.remaining - 1);
          renderTimer();
          savePlayState(stored, state);
          if (state.remaining === 0 && timerId) {
            window.clearInterval(timerId);
            timerId = null;
            renderTimer();
          }
        }, 1000);
      }
      renderTimer();
    });

    document.querySelector<HTMLButtonElement>("#timerReset")?.addEventListener("click", () => {
      state.remaining = game.boardMinutes * 60;
      if (timerId) window.clearInterval(timerId);
      timerId = null;
      renderTimer();
      savePlayState(stored, state);
    });

    document.querySelector<HTMLButtonElement>("#resetBoard")?.addEventListener("click", () => {
      state.scores = [0, 0];
      state.used = {};
      state.remaining = game.boardMinutes * 60;
      finalResolved = [false, false];
      savePlayState(stored, state);
      renderBoard();
      renderScores();
      renderTimer();
      showToast("Board reset.");
    });

    document.querySelector<HTMLButtonElement>("#fullscreenButton")?.addEventListener("click", async () => {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen().catch(() => undefined);
      } else {
        await document.exitFullscreen().catch(() => undefined);
      }
    });

    document.querySelector<HTMLButtonElement>("#duplicateButton")?.addEventListener("click", async () => {
      try {
        const response = await duplicateGame(stored.slug);
        showToast("Duplicate created.");
        navigate(response.editUrl);
      } catch (error) {
        showToast(error instanceof Error ? error.message : "Could not duplicate game.");
      }
    });

    document.querySelector<HTMLButtonElement>("#editGameButton")?.addEventListener("click", () => {
      const editToken = stored.slug === GUYMAN_SLUG ? GUYMAN_SEED_EDIT_TOKEN : getStoredEditToken(stored.slug);
      if (!editToken) {
        showToast("Use the private edit link for this game.");
        return;
      }
      navigate(`/edit/${stored.slug}/${editToken}`);
    });

    document.querySelector<HTMLButtonElement>("#finalButton")?.addEventListener("click", () => {
      updateFinalLabels();
      document.querySelector<HTMLElement>("#finalAnswerWrap")!.classList.remove("show");
      showDialog("finalDialog");
    });

    document.querySelector<HTMLButtonElement>("#revealButton")?.addEventListener("click", () => {
      document.querySelector<HTMLElement>("#modalAnswerWrap")!.classList.add("show");
    });

    document.querySelector<HTMLButtonElement>("#closeDialog")?.addEventListener("click", () => closeDialog("clueDialog"));
    document.querySelector<HTMLButtonElement>("#noPoints")?.addEventListener("click", () => closeActiveClue());
    document.querySelector<HTMLButtonElement>("#awardTeam0")?.addEventListener("click", () => awardActiveClue(0, 1));
    document.querySelector<HTMLButtonElement>("#awardTeam1")?.addEventListener("click", () => awardActiveClue(1, 1));
    document.querySelector<HTMLButtonElement>("#penaltyTeam0")?.addEventListener("click", () => awardActiveClue(0, -1));
    document.querySelector<HTMLButtonElement>("#penaltyTeam1")?.addEventListener("click", () => awardActiveClue(1, -1));

    document.querySelector<HTMLButtonElement>("#finalReveal")?.addEventListener("click", () => {
      document.querySelector<HTMLElement>("#finalAnswerWrap")!.classList.add("show");
    });
    document.querySelector<HTMLButtonElement>("#finalClose")?.addEventListener("click", () => closeDialog("finalDialog"));
    document.querySelector<HTMLButtonElement>("#finalTeam0Right")?.addEventListener("click", () => resolveFinal(0, 1));
    document.querySelector<HTMLButtonElement>("#finalTeam1Right")?.addEventListener("click", () => resolveFinal(1, 1));
    document.querySelector<HTMLButtonElement>("#finalTeam0Wrong")?.addEventListener("click", () => resolveFinal(0, -1));
    document.querySelector<HTMLButtonElement>("#finalTeam1Wrong")?.addEventListener("click", () => resolveFinal(1, -1));
  }

  function renderBoard(): void {
    const board = document.querySelector<HTMLElement>("#board")!;
    board.innerHTML = "";

    game.categories.forEach((category, categoryIndex) => {
      const header = document.createElement("div");
      header.className = "category";
      header.textContent = category.category;
      board.appendChild(header);

      category.clues.forEach((clue, clueIndex) => {
        const id = clueId(categoryIndex, clueIndex);
        const tile = document.createElement("button");
        tile.type = "button";
        tile.className = `clue-tile${state.used[id] ? " used" : ""}`;
        tile.dataset.testid = `clue-${categoryIndex}-${clueIndex}`;
        tile.textContent = String(clue.value);
        tile.addEventListener("click", () => openClue(categoryIndex, clueIndex));
        board.appendChild(tile);
      });
    });

    const total = game.categories.reduce((count, category) => count + category.clues.length, 0);
    const claimed = Object.values(state.used).filter(Boolean).length;
    setText("#clueCount", `${claimed} of ${total} claimed`);
    document.querySelector<HTMLElement>("#progressFill")!.style.width = total ? `${(claimed / total) * 100}%` : "0%";
  }

  function openClue(categoryIndex: number, clueIndex: number): void {
    const category = game.categories[categoryIndex];
    const clue = category.clues[clueIndex];
    active = { categoryIndex, clueIndex, clue, category };
    setText("#modalCategory", category.category);
    setText("#modalValue", String(clue.value));
    setText("#modalClue", clue.clue);
    setText("#modalAnswer", clue.answer);
    setText("#modalSource", clue.source || "");
    document.querySelector<HTMLElement>("#modalAnswerWrap")!.classList.remove("show");
    updateAwardLabels();
    showDialog("clueDialog");
  }

  function awardActiveClue(team: number, direction: 1 | -1): void {
    if (!active) return;
    adjustScore(team, active.clue.value * direction);
    closeActiveClue();
  }

  function closeActiveClue(): void {
    if (active) {
      state.used[clueId(active.categoryIndex, active.clueIndex)] = true;
      savePlayState(stored, state);
      renderBoard();
    }
    active = null;
    closeDialog("clueDialog");
  }

  function adjustScore(team: number, delta: number): void {
    state.scores[team] += delta;
    savePlayState(stored, state);
    renderScores();
  }

  function renderScores(): void {
    setText("#team0Score", String(state.scores[0]));
    setText("#team1Score", String(state.scores[1]));
    updateAwardLabels();
    updateFinalLabels();
  }

  function renderTimer(): void {
    const minutes = Math.floor(state.remaining / 60).toString().padStart(2, "0");
    const seconds = (state.remaining % 60).toString().padStart(2, "0");
    setText("#timeDisplay", `${minutes}:${seconds}`);
    setText("#timerToggle", timerId ? "Pause" : "Start");
  }

  function updateAwardLabels(): void {
    const value = active?.clue.value || 0;
    setText("#awardTeam0", `${state.names[0]} +${value}`);
    setText("#awardTeam1", `${state.names[1]} +${value}`);
    setText("#penaltyTeam0", `${state.names[0]} -${value}`);
    setText("#penaltyTeam1", `${state.names[1]} -${value}`);
  }

  function updateFinalLabels(): void {
    setText("#finalTeam0Right", `${state.names[0]} Right`);
    setText("#finalTeam1Right", `${state.names[1]} Right`);
    setText("#finalTeam0Wrong", `${state.names[0]} Wrong`);
    setText("#finalTeam1Wrong", `${state.names[1]} Wrong`);
    const wager0 = document.querySelector<HTMLInputElement>("#team0Wager");
    const wager1 = document.querySelector<HTMLInputElement>("#team1Wager");
    if (wager0) wager0.previousElementSibling!.textContent = `${state.names[0]} Wager`;
    if (wager1) wager1.previousElementSibling!.textContent = `${state.names[1]} Wager`;
  }

  function resolveFinal(team: number, direction: 1 | -1): void {
    if (finalResolved[team]) {
      showToast("That team's final wager is already resolved.");
      return;
    }
    const input = document.querySelector<HTMLInputElement>(`#team${team}Wager`)!;
    const wager = Math.max(0, Number.parseInt(input.value || "0", 10) || 0);
    finalResolved[team] = true;
    adjustScore(team, wager * direction);
  }
}

function renderCategoryEditor(categories: Category[]): void {
  const container = document.querySelector<HTMLElement>("#categoryEditor")!;
  container.innerHTML = "";

  categories.forEach((category, categoryIndex) => {
    const section = document.createElement("section");
    section.className = "form-band";
    section.innerHTML = `
      <h2>Category ${categoryIndex + 1}</h2>
      ${textInput(`category-${categoryIndex}`, "Category name", category.category)}
      <div class="clue-editor" data-category="${categoryIndex}"></div>
    `;
    container.appendChild(section);

    const clueEditor = section.querySelector<HTMLElement>(".clue-editor")!;
    category.clues.forEach((clue, clueIndex) => {
      const clueSection = document.createElement("article");
      clueSection.className = "clue-edit-card";
      clueSection.innerHTML = `
        <h3>${clue.value} Points</h3>
        ${numberInput(`clue-${categoryIndex}-${clueIndex}-value`, "Value", clue.value)}
        ${textareaInput(`clue-${categoryIndex}-${clueIndex}-clue`, "Question", clue.clue)}
        ${textareaInput(`clue-${categoryIndex}-${clueIndex}-answer`, "Answer", clue.answer)}
        ${textInput(`clue-${categoryIndex}-${clueIndex}-source`, "Source", clue.source || "")}
      `;
      clueEditor.appendChild(clueSection);
    });
  });
}

function readGameForm(): GameData {
  const categories: Category[] = [];
  for (let categoryIndex = 0; categoryIndex < 5; categoryIndex += 1) {
    const clues: Clue[] = [];
    for (let clueIndex = 0; clueIndex < 4; clueIndex += 1) {
      clues.push({
        value: getNumber(`clue-${categoryIndex}-${clueIndex}-value`, (clueIndex + 1) * 100),
        clue: getValue(`clue-${categoryIndex}-${clueIndex}-clue`),
        answer: getValue(`clue-${categoryIndex}-${clueIndex}-answer`),
        source: getValue(`clue-${categoryIndex}-${clueIndex}-source`)
      });
    }
    categories.push({
      category: getValue(`category-${categoryIndex}`),
      clues
    });
  }

  const theme = getValue("theme") || "Trivia Game";
  return {
    title: getValue("title") || theme,
    eyebrow: getValue("eyebrow") || "Game Night",
    subtitle: getValue("subtitle") || "Jeopardy Board",
    theme,
    teamNames: [getValue("team0") || "Team 1", getValue("team1") || "Team 2"],
    boardMinutes: getNumber("boardMinutes", 18),
    sourceNote: getValue("sourceNote"),
    categories,
    final: {
      clue: getValue("finalClue"),
      answer: getValue("finalAnswer"),
      source: getValue("finalSource")
    }
  };
}

function makeBlankGame(): GameData {
  const categories = Array.from({ length: 5 }, (_, categoryIndex) => ({
    category: `Category ${categoryIndex + 1}`,
    clues: [100, 200, 300, 400].map((value) => ({
      value,
      clue: "",
      answer: "",
      source: ""
    }))
  }));

  return {
    title: "Tribal Trivia",
    eyebrow: "Game Night",
    subtitle: "Custom Jeopardy",
    theme: "My Trivia Game",
    teamNames: ["Team 1", "Team 2"],
    boardMinutes: 18,
    sourceNote: "",
    categories,
    final: {
      clue: "",
      answer: "",
      source: ""
    }
  };
}

function loadPlayState(stored: StoredGame): PlayState {
  const fallback: PlayState = {
    scores: [0, 0],
    used: {},
    names: stored.game.teamNames,
    remaining: stored.game.boardMinutes * 60
  };

  try {
    const saved = JSON.parse(localStorage.getItem(playStateKey(stored.slug)) || "null") as Partial<PlayState> | null;
    if (!saved || !Array.isArray(saved.scores)) return fallback;
    return {
      scores: [Number(saved.scores[0]) || 0, Number(saved.scores[1]) || 0],
      used: saved.used || {},
      names: Array.isArray(saved.names) ? [saved.names[0] || fallback.names[0], saved.names[1] || fallback.names[1]] : fallback.names,
      remaining: Number.isFinite(saved.remaining) ? Number(saved.remaining) : fallback.remaining
    };
  } catch {
    return fallback;
  }
}

function savePlayState(stored: StoredGame, state: PlayState): void {
  localStorage.setItem(playStateKey(stored.slug), JSON.stringify(state));
}

function playStateKey(slug: string): string {
  return `tribal-trivia-play-state-${slug}`;
}

function textInput(name: string, label: string, value: string): string {
  return `<label>${label}<input name="${name}" value="${escapeAttr(value)}"></label>`;
}

function numberInput(name: string, label: string, value: number): string {
  return `<label>${label}<input name="${name}" type="number" min="0" value="${value}"></label>`;
}

function textareaInput(name: string, label: string, value: string): string {
  return `<label class="wide">${label}<textarea name="${name}">${escapeHtml(value)}</textarea></label>`;
}

function getValue(name: string): string {
  const field = document.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${name}"]`);
  return field?.value.trim() || "";
}

function getNumber(name: string, fallback: number): number {
  const value = Number.parseInt(getValue(name), 10);
  return Number.isFinite(value) ? value : fallback;
}

function teamHtml(team: 0 | 1, name: string): string {
  return `
    <article class="team">
      <input data-team-name="${team}" aria-label="Team ${team + 1} name" value="${escapeAttr(name)}">
      <div class="score" id="team${team}Score">0</div>
      <div class="score-controls">
        <button type="button" data-testid="team-${team}-plus" data-score-team="${team}" data-score-delta="100" aria-label="Add 100 to Team ${team + 1}">+</button>
        <button type="button" data-testid="team-${team}-minus" data-score-team="${team}" data-score-delta="-100" aria-label="Subtract 100 from Team ${team + 1}">-</button>
      </div>
    </article>
  `;
}

function clueDialogHtml(): string {
  return `
    <dialog id="clueDialog">
      <div class="modal-shell">
        <div class="modal-top">
          <p class="modal-kicker" id="modalCategory">Category</p>
          <div class="modal-value" id="modalValue">100</div>
        </div>
        <div class="clue-text" id="modalClue">Clue text</div>
        <section class="answer" id="modalAnswerWrap" aria-live="polite">
          <p class="answer-label">Correct Response</p>
          <p class="answer-text" id="modalAnswer">Answer</p>
          <p class="source" id="modalSource"></p>
        </section>
        <div class="modal-actions">
          <button class="modal-btn" id="revealButton" data-testid="clue-reveal" type="button">Reveal Answer</button>
          <div class="award-actions">
            <button class="modal-btn award" id="awardTeam0" data-testid="clue-team-0-award" type="button">Team 1 +</button>
            <button class="modal-btn award" id="awardTeam1" data-testid="clue-team-1-award" type="button">Team 2 +</button>
            <button class="modal-btn penalty" id="penaltyTeam0" data-testid="clue-team-0-penalty" type="button">Team 1 -</button>
            <button class="modal-btn penalty" id="penaltyTeam1" data-testid="clue-team-1-penalty" type="button">Team 2 -</button>
            <button class="modal-btn neutral" id="noPoints" data-testid="clue-no-points" type="button">No Points</button>
            <button class="modal-btn neutral" id="closeDialog" data-testid="clue-close" type="button">Close</button>
          </div>
        </div>
      </div>
    </dialog>
  `;
}

function finalDialogHtml(): string {
  return `
    <dialog id="finalDialog">
      <div class="modal-shell">
        <div class="modal-top">
          <p class="modal-kicker">Final Tribal Council</p>
          <div class="modal-value">Final</div>
        </div>
        <div class="clue-text" id="finalClue"></div>
        <section class="answer" id="finalAnswerWrap" aria-live="polite">
          <p class="answer-label">Correct Response</p>
          <p class="answer-text" id="finalAnswer"></p>
          <p class="source" id="finalSource"></p>
        </section>
        <div class="final-grid">
          <div class="wager-box">
            <label for="team0Wager">Team 1 Wager</label>
            <input id="team0Wager" inputmode="numeric" value="0">
          </div>
          <div class="wager-box">
            <label for="team1Wager">Team 2 Wager</label>
            <input id="team1Wager" inputmode="numeric" value="0">
          </div>
        </div>
        <div class="modal-actions">
          <button class="modal-btn" id="finalReveal" data-testid="final-reveal" type="button">Reveal Answer</button>
          <div class="award-actions">
            <button class="modal-btn award" id="finalTeam0Right" data-testid="final-team-0-right" type="button">Team 1 Right</button>
            <button class="modal-btn award" id="finalTeam1Right" data-testid="final-team-1-right" type="button">Team 2 Right</button>
            <button class="modal-btn penalty" id="finalTeam0Wrong" data-testid="final-team-0-wrong" type="button">Team 1 Wrong</button>
            <button class="modal-btn penalty" id="finalTeam1Wrong" data-testid="final-team-1-wrong" type="button">Team 2 Wrong</button>
            <button class="modal-btn neutral" id="finalClose" data-testid="final-close" type="button">Close</button>
          </div>
        </div>
      </div>
    </dialog>
  `;
}

function renderLoading(message: string): void {
  appRoot.innerHTML = `<main class="status-shell"><div class="status-panel">${escapeHtml(message)}...</div></main>`;
}

function renderError(error: unknown): void {
  const message = error instanceof ApiError || error instanceof Error ? error.message : "Something went wrong.";
  appRoot.innerHTML = `
    <main class="status-shell">
      <section class="status-panel">
        <h1>Unable to load</h1>
        <p>${escapeHtml(message)}</p>
        <a class="button primary" href="/" data-link>Home</a>
      </section>
    </main>
  `;
  wireLinks();
}

function renderNotFound(): void {
  appRoot.innerHTML = `
    <main class="status-shell">
      <section class="status-panel">
        <h1>Not Found</h1>
        <p>This game link does not exist.</p>
        <a class="button primary" href="/" data-link>Home</a>
      </section>
    </main>
  `;
  wireLinks();
}

function showDialog(id: string): void {
  const dialog = document.querySelector<HTMLDialogElement>(`#${id}`)!;
  if (typeof dialog.showModal === "function") {
    dialog.showModal();
  } else {
    dialog.setAttribute("open", "");
  }
}

function closeDialog(id: string): void {
  const dialog = document.querySelector<HTMLDialogElement>(`#${id}`)!;
  if (typeof dialog.close === "function") {
    dialog.close();
  } else {
    dialog.removeAttribute("open");
  }
}

function showToast(message: string): void {
  const toast = document.querySelector<HTMLElement>("#toast");
  if (!toast) return;
  toast.textContent = message;
  toast.classList.add("show");
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toast.classList.remove("show"), 2200);
}

function setText(selector: string, value: string): void {
  const element = document.querySelector<HTMLElement>(selector);
  if (element) element.textContent = value;
}

function clueId(categoryIndex: number, clueIndex: number): string {
  return `${categoryIndex}-${clueIndex}`;
}

function absoluteUrl(path: string): string {
  return new URL(path, window.location.origin).toString();
}

function absoluteRouteUrl(path: string): string {
  if (path === "/") return absoluteUrl("/");
  return `${window.location.origin}/#${path}`;
}

function currentRoute(): string {
  if (window.location.hash.startsWith("#/")) {
    return window.location.hash.slice(1);
  }
  return window.location.pathname;
}

function navigate(path: string): void {
  if (path === "/") {
    history.pushState(null, "", "/");
  } else if (path.startsWith("#/")) {
    history.pushState(null, "", path);
  } else {
    history.pushState(null, "", `#${path}`);
  }
  void renderRoute();
}

function wireLinks(): void {
  document.querySelectorAll<HTMLAnchorElement>("a[data-link]").forEach((link) => {
    link.addEventListener("click", (event) => {
      const href = link.getAttribute("href");
      if (!href || href.startsWith("http")) return;
      event.preventDefault();
      navigate(href);
    });
  });
}

function setNoIndex(): void {
  document.querySelector<HTMLMetaElement>("#robotsMeta")?.setAttribute("content", "noindex, nofollow");
}

function clearRobots(): void {
  document.querySelector<HTMLMetaElement>("#robotsMeta")?.setAttribute("content", "index, follow");
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function escapeAttr(value: string): string {
  return escapeHtml(value);
}

function badgeSvg(): string {
  return `
    <svg class="badge" viewBox="0 0 120 120" role="img" aria-label="Island badge">
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#f0b33f"/>
          <stop offset=".48" stop-color="#d85f38"/>
          <stop offset="1" stop-color="#0f6f72"/>
        </linearGradient>
      </defs>
      <path d="M60 6 105 22 114 67 86 106 34 106 6 67 15 22Z" fill="#07161b" stroke="#e1c16e" stroke-width="4"/>
      <path d="M60 13 98 27 106 65 82 98 38 98 14 65 22 27Z" fill="url(#sky)" opacity=".95"/>
      <path d="M22 76c13-13 25-16 37-10 12-8 25-5 39 10v22H22Z" fill="#10242b"/>
      <path d="M41 63c10-21 27-29 45-36-15 12-25 25-30 44Z" fill="#4c8c3d"/>
      <path d="M42 62c-1-21-10-33-25-42 20 4 33 16 39 39Z" fill="#4c8c3d"/>
      <path d="M54 91c5-27 7-46 4-58" fill="none" stroke="#4b2d1a" stroke-width="7" stroke-linecap="round"/>
      <path d="M36 80h48" stroke="#e1c16e" stroke-width="5" stroke-linecap="round"/>
      <text x="60" y="90" text-anchor="middle" font-family="Arial, sans-serif" font-size="25" font-weight="900" fill="#f8f3e7">150</text>
    </svg>
  `;
}

void slugify(guymanGame.slug);
